/**
 * MoveCrewTrucksPage — Crew & Trucks sub-page for /moves/:quoteId/crew-trucks
 *
 * Only visible when lifecycleStage === 'job'. Mirrors the Crew & Trucks tab
 * from the old JobDetailPage with identical layout and functionality.
 */
import { useState } from 'react';
import { createPortal } from 'react-dom';
import { useNavigate, useParams } from 'react-router-dom';
import {
  ArrowLeft,
  AlertCircle,
  ChevronRight,
  Clock,
  HardHat,
  Pencil,
  Plus,
  Route,
  Star,
  Car as SteeringWheel,
  Truck,
  Users,
  X,
} from 'lucide-react';
import { useSharedQuote } from '../../context/QuoteContext';
import { useUserProfile } from '../../context/UserProfileContext';
import { useJob, AssignedTruck } from '../../hooks/useJobs';
import { useFleet } from '../../hooks/useFleet';
import { LoadingSpinner } from '../../components/ui/LoadingSpinner';
import { CrewAssignmentModal } from '../../components/jobs/CrewAssignmentModal';
import { TruckAssignmentModal } from '../../components/jobs/TruckAssignmentModal';
import type { Truck as TruckData } from '../../types/admin';

// ── Helpers ──────────────────────────────────────────────────────────────────

const TRUCK_TYPE_LABELS: Record<string, string> = {
  cargo_van: 'Cargo Van',
  sprinter_van: 'Sprinter Van',
  '12ft_box': '12ft Box',
  '16ft_box': '16ft Box',
  '20ft_box': '20ft Box',
  '24ft_box': '24ft Box',
  '26ft_box': '26ft Box',
  flatbed: 'Flatbed',
  other: 'Other',
};

// ── Page ─────────────────────────────────────────────────────────────────────

export function MoveCrewTrucksPage() {
  const { quoteId } = useParams<{ quoteId: string }>();
  const navigate = useNavigate();
  const { activeOrg } = useUserProfile();
  const orgId = activeOrg?.orgId ?? null;
  const { quote, loading: quoteLoading } = useSharedQuote();

  // Fetch job data (contains assignedCrew, assignedTrucks, logisticsSummary)
  const { job, loading: jobLoading, updateJobCrew, updateJobTrucks } = useJob(orgId, quoteId ?? null);

  // Fetch fleet data for truck details and warehouse name resolution
  const { trucks: fleetTrucks, warehouses } = useFleet(orgId);

  const [showCrewModal, setShowCrewModal] = useState(false);
  const [showTruckModal, setShowTruckModal] = useState(false);

  const isLoading = quoteLoading || jobLoading;

  // Build truck lookup map
  const truckMap = new Map<string, TruckData>();
  for (const t of fleetTrucks) truckMap.set(t.id, t);

  // Build warehouse name lookup
  const warehouseMap = new Map<string, string>();
  for (const w of warehouses) warehouseMap.set(w.id, w.name);

  if (isLoading && !job) {
    return (
      <div className="flex justify-center py-16">
        <LoadingSpinner size="lg" />
      </div>
    );
  }

  if (!quote || !job) {
    return (
      <div className="px-4 pt-6 flex flex-col items-center py-20 gap-2 text-center">
        <AlertCircle size={32} className="text-rose/50 mb-2" />
        <p className="text-small text-rose font-semibold">Record not found</p>
        <button type="button" onClick={() => navigate(-1)}
          className="text-[13px] text-plum hover:underline mt-2">Go Back</button>
      </div>
    );
  }

  const ls = job.logisticsSummary;
  const crewNeeded = ls?.crewSize ?? 0;
  const trucksNeeded = ls?.truckQuantity ?? 0;
  const assignedCrew = job.assignedCrew ?? [];
  const assignedTrucks = job.assignedTrucks ?? [];
  const crewCount = assignedCrew.length;
  const truckCount = assignedTrucks.length;

  const quoteType = job.quoteType ?? 'commercial';
  const driverQuantity = ls?.driverQuantity ?? trucksNeeded;
  const driverCount = assignedCrew.filter(c => c.isDriver).length;

  return (
    <div className="px-4 pt-4 pb-8">
      {/* Mobile back button */}
      <button type="button" onClick={() => navigate(-1)}
        className="md:hidden flex items-center gap-1 text-small text-slate hover:text-midnight mb-4
                   transition-colors duration-150 -ml-1">
        <ArrowLeft size={16} />
        Back
      </button>

      <h1 className="font-display text-h3 text-midnight mb-4">Crew & Trucks</h1>

      <div className="flex flex-col gap-4 lg:grid lg:grid-cols-2 lg:gap-5">
        {/* ── Summary Card ───────────────────────────────────────── */}
        {ls && (
          <div className="bg-white border border-stone rounded-xl p-4 shadow-brand-sm">
            <div className="flex items-center gap-2 mb-3">
              <Truck size={15} className="text-plum" />
              <p className="text-[13px] font-semibold text-midnight">Summary</p>
            </div>
            <div className="flex items-center gap-4">
              {ls.crewSize != null && (
                <div className="flex-1 flex flex-col items-center gap-1">
                  <Users size={18} className="text-plum/60" />
                  <p className="font-bold text-[20px] text-midnight leading-none">{ls.crewSize}</p>
                  <p className="text-[11px] text-slate">Crew</p>
                </div>
              )}
              {ls.truckQuantity != null && (
                <div className="flex-1 flex flex-col items-center gap-1">
                  <Truck size={18} className="text-blue-400" />
                  <p className="font-bold text-[20px] text-midnight leading-none">{ls.truckQuantity}</p>
                  <p className="text-[11px] text-slate">Trucks</p>
                </div>
              )}
              {ls.estimatedHours != null && (
                <div className="flex-1 flex flex-col items-center gap-1">
                  <Clock size={18} className="text-amber-400" />
                  <p className="font-bold text-[20px] text-midnight leading-none">
                    {ls.estimatedHours.toLocaleString('en-US', { maximumFractionDigits: 1 })}
                  </p>
                  <p className="text-[11px] text-slate">Hours</p>
                </div>
              )}
              {quoteType === 'commercial' && ls.method && (
                <div className="flex-1 flex flex-col items-center gap-1">
                  <Route size={18} className="text-emerald-400" />
                  <p className="font-bold text-[14px] text-midnight leading-none">{ls.method}</p>
                  <p className="text-[11px] text-slate">Method</p>
                </div>
              )}
            </div>
          </div>
        )}

        {/* ── Crew Section ───────────────────────────────────────── */}
        <div className="bg-white border border-stone rounded-xl p-4 shadow-brand-sm">
          <div className="flex items-center justify-between mb-3">
            <div className="flex items-center gap-2">
              <Users size={15} className="text-plum" />
              <p className="text-[13px] font-semibold text-midnight">Crew</p>
            </div>
            <div className="text-right">
              <p className="text-[13px] text-slate">
                <span className={`font-bold ${crewCount >= crewNeeded && crewNeeded > 0 ? 'text-emerald-600' : 'text-midnight'}`}>{crewCount}</span>
                {' / '}{crewNeeded} assigned
              </p>
              {driverQuantity > 0 && (
                <p className="text-[11px] text-slate mt-0.5">
                  <span className={`font-bold ${driverCount >= driverQuantity ? 'text-emerald-600' : 'text-midnight'}`}>{driverCount}</span>
                  {' / '}{driverQuantity} drivers
                </p>
              )}
            </div>
          </div>

          {assignedCrew.length > 0 ? (
            <ul className="flex flex-col gap-1.5 mb-3">
              {assignedCrew.map((member, i) => (
                <li key={member.userId ?? `contractor-${i}`}
                  className="flex items-center gap-2 px-3 py-2 rounded-lg border border-stone/60 bg-mist/30">
                  <div className="w-7 h-7 rounded-full bg-plum/10 flex items-center justify-center shrink-0 text-[12px] font-semibold text-plum">
                    {member.displayName.charAt(0).toUpperCase()}
                  </div>
                  <p className="flex-1 text-[13px] font-medium text-midnight truncate flex items-center gap-1.5">
                    {member.displayName}
                    {member.isCrewLead && (
                      <span className="inline-flex items-center gap-0.5 px-1.5 py-0.5 rounded-md bg-amber-100 text-amber-700 text-[9px] font-bold uppercase">
                        <Star size={9} /> Lead
                      </span>
                    )}
                    {member.isDriver && (
                      <span className="inline-flex items-center gap-0.5 px-1.5 py-0.5 rounded-md bg-blue-50 text-blue-600 text-[9px] font-bold uppercase">
                        <SteeringWheel size={9} /> Driver
                      </span>
                    )}
                    {member.isContractor && (
                      <span className="inline-flex items-center gap-0.5 px-1.5 py-0.5 rounded-md bg-stone text-slate text-[9px] font-bold uppercase">
                        <HardHat size={9} /> Contractor
                      </span>
                    )}
                  </p>
                  <button type="button" onClick={() => {
                    const next = assignedCrew.filter((_, j) => j !== i);
                    if (assignedCrew[i].isCrewLead && next.length > 0) next[0] = { ...next[0], isCrewLead: true };
                    updateJobCrew(next);
                  }}
                    className="w-7 h-7 flex items-center justify-center rounded-lg text-slate hover:text-rose hover:bg-rose/10 transition-colors shrink-0"
                    aria-label="Remove">
                    <X size={13} />
                  </button>
                </li>
              ))}
            </ul>
          ) : (
            <div className="text-center py-4 mb-3">
              <Users size={28} className="mx-auto text-slate/30 mb-1" />
              <p className="text-[12px] text-slate">No crew assigned yet</p>
            </div>
          )}

          {crewCount < crewNeeded && orgId && (
            <button type="button" onClick={() => setShowCrewModal(true)}
              className="w-full flex items-center justify-center gap-2 h-10 rounded-xl border border-dashed border-plum/30
                         text-plum text-[13px] font-medium hover:bg-plum/5 hover:border-plum/50 transition-colors">
              <Plus size={14} />
              Assign Crew
            </button>
          )}
          {crewCount >= crewNeeded && crewNeeded > 0 && orgId && (
            <button type="button" onClick={() => setShowCrewModal(true)}
              className="w-full flex items-center justify-center gap-2 h-10 rounded-xl border border-stone/60
                         text-slate text-[13px] font-medium hover:bg-plum/5 hover:text-plum hover:border-plum/30 transition-colors">
              <Pencil size={13} />
              Update Assignments
            </button>
          )}
        </div>

        {/* ── Trucks Section ──────────────────────────────────────── */}
        <div className="bg-white border border-stone rounded-xl p-4 shadow-brand-sm">
          <div className="flex items-center justify-between mb-3">
            <div className="flex items-center gap-2">
              <Truck size={15} className="text-plum" />
              <p className="text-[13px] font-semibold text-midnight">Trucks</p>
            </div>
            <p className="text-[13px] text-slate">
              <span className={`font-bold ${truckCount >= trucksNeeded && trucksNeeded > 0 ? 'text-emerald-600' : 'text-midnight'}`}>{truckCount}</span>
              {' / '}{trucksNeeded} assigned
            </p>
          </div>

          {assignedTrucks.length > 0 ? (
            <ul className="flex flex-col gap-1.5 mb-3">
              {assignedTrucks.map((truck, i) => (
                <li key={truck.truckId} className="flex items-center gap-2">
                  <div className="flex-1 min-w-0">
                    <TruckCompactCard assigned={truck} truckData={truckMap.get(truck.truckId)} warehouseMap={warehouseMap} />
                  </div>
                  <button type="button" onClick={() => {
                    updateJobTrucks(assignedTrucks.filter((_, j) => j !== i));
                  }}
                    className="w-7 h-7 flex items-center justify-center rounded-lg text-slate hover:text-rose hover:bg-rose/10 transition-colors shrink-0"
                    aria-label="Remove">
                    <X size={13} />
                  </button>
                </li>
              ))}
            </ul>
          ) : (
            <div className="text-center py-4 mb-3">
              <Truck size={28} className="mx-auto text-slate/30 mb-1" />
              <p className="text-[12px] text-slate">No trucks assigned yet</p>
            </div>
          )}

          {truckCount < trucksNeeded && orgId && (
            <button type="button" onClick={() => setShowTruckModal(true)}
              className="w-full flex items-center justify-center gap-2 h-10 rounded-xl border border-dashed border-blue-300
                         text-blue-600 text-[13px] font-medium hover:bg-blue-50 hover:border-blue-400 transition-colors">
              <Plus size={14} />
              Assign Truck{trucksNeeded - truckCount > 1 ? 's' : ''}
            </button>
          )}
        </div>
      </div>

      {/* ── Modals ──────────────────────────────────────────────── */}
      {showCrewModal && orgId && quoteId && (
        <CrewAssignmentModal
          orgId={orgId}
          jobId={quoteId}
          moveDate={job.movingDate?.preferredDate}
          crewNeeded={crewNeeded}
          driverQuantity={driverQuantity}
          quoteType={quoteType}
          currentCrew={assignedCrew}
          onSave={updateJobCrew}
          onClose={() => setShowCrewModal(false)}
        />
      )}
      {showTruckModal && orgId && quoteId && (
        <TruckAssignmentModal
          orgId={orgId}
          jobId={quoteId}
          moveDate={job.movingDate?.preferredDate}
          trucksNeeded={trucksNeeded}
          currentTrucks={assignedTrucks}
          onSave={updateJobTrucks}
          onClose={() => setShowTruckModal(false)}
        />
      )}
    </div>
  );
}

// ── Truck Compact Card + Detail Modal ────────────────────────────────────────

function TruckCompactCard({ assigned, truckData, warehouseMap }: { assigned: AssignedTruck; truckData?: TruckData; warehouseMap?: Map<string, string> }) {
  const [showDetail, setShowDetail] = useState(false);
  const makeModel = [truckData?.make, truckData?.model].filter(Boolean).join(' ');

  function resolveLocation(loc?: string) {
    if (!loc) return undefined;
    if (loc === 'On Job') return 'On Job';
    return warehouseMap?.get(loc) ?? loc;
  }

  return (
    <>
      <button type="button" onClick={() => setShowDetail(true)}
        className="w-full text-left px-3 py-2 rounded-lg border border-stone/60 bg-mist/30
                   hover:bg-mist hover:border-stone transition-colors">
        <div className="flex items-center gap-2">
          <div className="w-8 h-8 rounded-lg bg-blue-50 flex items-center justify-center shrink-0">
            <Truck size={14} className="text-blue-600" />
          </div>
          <div className="flex-1 min-w-0">
            <p className="text-[13px] font-medium text-midnight truncate">{assigned.truckName}</p>
            <p className="text-[11px] text-slate truncate">
              {[makeModel, resolveLocation(truckData?.currentLocation)].filter(Boolean).join(' · ') || 'No details'}
            </p>
          </div>
          <ChevronRight size={14} className="text-slate shrink-0" />
        </div>
      </button>
      {showDetail && truckData && (
        <TruckDetailModal truck={truckData} warehouseMap={warehouseMap} onClose={() => setShowDetail(false)} />
      )}
    </>
  );
}

function TruckDetailModal({ truck, warehouseMap, onClose }: { truck: TruckData; warehouseMap?: Map<string, string>; onClose: () => void }) {
  return createPortal(
    <div className="fixed inset-0 z-50 bg-black/40 flex items-end sm:items-center justify-center"
      onClick={onClose}>
      <div className="bg-white w-full max-w-md rounded-t-2xl sm:rounded-2xl p-5 max-h-[80vh] overflow-y-auto"
        onClick={e => e.stopPropagation()}>
        <div className="flex items-center justify-between mb-4">
          <h3 className="font-display text-[16px] font-semibold text-midnight">{truck.name}</h3>
          <button type="button" onClick={onClose}
            className="w-8 h-8 flex items-center justify-center rounded-lg text-slate hover:bg-mist transition-colors">
            <X size={16} />
          </button>
        </div>
        <div className="flex flex-col gap-2 text-[13px]">
          {truck.type && <DetailRow label="Type" value={TRUCK_TYPE_LABELS[truck.type] ?? truck.type} />}
          {truck.year && <DetailRow label="Year" value={String(truck.year)} />}
          {truck.make && <DetailRow label="Make" value={truck.make} />}
          {truck.model && <DetailRow label="Model" value={truck.model} />}
          {truck.licensePlate && <DetailRow label="License Plate" value={truck.licensePlate} />}
          {truck.capacity != null && <DetailRow label="Capacity" value={`${truck.capacity} cu ft`} />}
          {truck.weightLimit != null && <DetailRow label="Weight Limit" value={`${truck.weightLimit} lbs`} />}
          {truck.currentLocation && (
            <DetailRow
              label="Current Location"
              value={truck.currentLocation === 'On Job' ? 'On Job' : (warehouseMap?.get(truck.currentLocation) ?? truck.currentLocation)}
            />
          )}
          {truck.status && <DetailRow label="Status" value={truck.status.charAt(0).toUpperCase() + truck.status.slice(1)} />}
          {truck.notes && <DetailRow label="Notes" value={truck.notes} />}
        </div>
      </div>
    </div>,
    document.body,
  );
}

function DetailRow({ label, value }: { label: string; value: string }) {
  return (
    <div className="flex justify-between gap-3">
      <span className="text-slate shrink-0">{label}</span>
      <span className="text-midnight font-medium text-right">{value}</span>
    </div>
  );
}
